<template>
  <div>
    <Modal v-model="commonLogModal" :title="title" :width="modalWidth" :transfer="false" @on-visible-change="commonLogChange">
      <slot></slot>
      <div class="table_box">
        <Table
          highlight-row
          border
          max-height="550"
          :loading="tableLoading"
          :columns="tableColumns"
          :data="tableData">
        </Table>
        <Page
          :transfer="true"
          v-if="showPage"
          :total="total"
          style="display: flex;justify-content: flex-end;margin-top: 10px"
          @on-change="changePage"
          show-total
          :page-size="pageParams.pageSize"
          show-elevator
          :current="pageParams.pageNum"
          show-sizer
          @on-page-size-change="changePageSize"
          placement="top"
          :page-size-opts="pageArray">
        </Page>
      </div>
      <template #footer>
        <Button type="primary" @click="commonLogModal = false">{{ $t('key1000316') }}</Button>
      </template>
    </Modal>
    <!--平台订单日志详情弹窗-->
    <platformOrderLogDetailsModal ref="platformOrderLogDetails"></platformOrderLogDetailsModal>
  </div>
</template>

<script>
import aliasb03645cf6abd4d56a73b13687765a5d8 from '@/locale/i18nUtils.js';

import api from '@/api/';
import Mixin from "@/components/mixin/common_mixin";
import {commonLocalStorage, getAllUsersInfo, getAllWarehouseData, getUserNameByUserId, isJSON} from "@/utils/common";
import {ATTRIBUTEMULTILINGUALNAME, ATTRIBUTEVALUEDATA} from "@/utils/enum";
import platformOrderLogDetailsModal from "@/views/distributor/components/orderManage/platformOrderLogDetailsModal.vue";

/**
 * ========================================================================
 * 日志请求策略配置表
 * ========================================================================
 * 每种日志模式（logMode）对应一个配置项，定义了：
 *   - getDefaultUrl(): 默认请求接口URL（调用方可通过 logApiUrl prop 覆盖）
 *   - buildParams():   请求参数构建函数
 *   - extractList():   从响应数据中提取列表数据的方式
 *   - isPagedResult:   响应是否为分页结构 {list, total}
 *   - filterEmpty:     是否需要过滤空 logContent 的数据
 */
const LOG_REQUEST_CONFIG = {
  /**
   * YMS标准日志模式
   * 适用场景：yms运营系统大部分页面的操作日志（客户、账单、IOSS、供应商管理等）
   * 请求参数：{ businessId, types }
   * 响应格式：直接返回数组
   */
  YMS_STANDARD: {
    getDefaultUrl: () => api.post_ymsLog_queryByBusinessId,
    buildParams(businessId, v) {
      return {businessId, types: v.logType};
    },
    extractList(data) {
      return Array.isArray(data) ? data : [];
    },
    isPagedResult: false,
    filterEmpty: false,
  },

  /**
   * 分销商日志模式
   * 适用场景：分销商系统各页面的操作日志（平台订单、备货、店铺、支付等）
   * 请求参数：分页参数 pageParams { pageNum, pageSize, businessId }
   * 响应格式：{ list, total } 分页结构
   * 特殊处理：分销商系统需过滤 contentType=48 的调价日志（在 getLogData 中处理）
   */
  DISTRIBUTOR: {
    getDefaultUrl: () => api.post_ymsLog_query,
    buildParams(businessId, v) {
      return v.pageParams;
    },
    extractList(data) {
      return data.list || [];
    },
    isPagedResult: true,
    filterEmpty: false,
  },

  /**
   * YMS查看分销商来源日志模式
   * 适用场景：yms系统中查看分销商类型的日志（如备货单日志、补贴审核日志）
   * 请求参数：{ businessId, types }
   * 响应格式：直接返回数组，需过滤空 logContent
   * 多语种处理：使用 'distributor' 作为 dynamicLog 的 system 参数
   */
  YMS_DISTRIBUTOR: {
    getDefaultUrl: () => api.post_ymsLog_api_queryByBusinessId,
    buildParams(businessId, v) {
      return {businessId, types: v.logType};
    },
    extractList(data) {
      return Array.isArray(data) ? data : [];
    },
    isPagedResult: false,
    filterEmpty: true,
  },

  /**
   * 自营商品日志模式
   * 适用场景：自营商品SPU/SKU的操作日志
   * 请求参数：根据 operationType 选择 productId(spu) 或 productGoodsId(sku) + 分页参数
   * 响应格式：{ list, total } 分页结构
   */
  SELF_OPERATED_PRODUCT: {
    getDefaultUrl: () => api.post_ymsProductInfoCore_queryLog,
    buildParams(businessId, v) {
      const {businessId: _, ...query} = v.pageParams;
      if (v.operationType === 'spu') query.productId = businessId;
      if (v.operationType === 'sku') query.productGoodsId = businessId;
      return query;
    },
    extractList(data) {
      return data.list || [];
    },
    isPagedResult: true,
    filterEmpty: false,
  },

  /**
   * 属性池日志模式
   * 适用场景：商品属性模板的操作日志（编辑属性/属性值的前后对比展示）
   * 请求参数：{ businessId, types }
   * 响应格式：直接返回数组
   * 特殊处理：不走 dynamicLog 多语种处理，有独立的结构化渲染逻辑（renderAttributePoolContent）
   */
  ATTRIBUTE_POOL: {
    getDefaultUrl: () => api.post_ymsLog_queryByBusinessId,
    buildParams(businessId, v) {
      return {businessId, types: v.logType};
    },
    extractList(data) {
      return Array.isArray(data) ? data : [];
    },
    isPagedResult: false,
    filterEmpty: false,
  },

  /**
   * 供应商仓储价格日志模式
   * 适用场景：供应商系统的仓储价格模型操作日志
   * 请求参数：{ businessId, types }
   * 响应格式：直接返回数组
   */
  SUPPLIER_WAREHOUSE: {
    getDefaultUrl: () => api.post_ymsWarehousePriceModel_queryWarehousePriceLog,
    buildParams(businessId, v) {
      return {businessId, types: v.logType};
    },
    extractList(data) {
      return Array.isArray(data) ? data : [];
    },
    isPagedResult: false,
    filterEmpty: false,
  },
};

/**
 * ========================================================================
 * 日志渲染策略配置表
 * ========================================================================
 * 每种日志模式（logMode）对应一个渲染配置：
 *   - renderer:          渲染方法名（standard / distributor / attributePool）
 *   - dynamicLogSystem:  传给 handleMultilingualLogs + dynamicLog 的 system 参数
 *   - useMultilingual:   是否走多语种处理管线（handleMultilingualLogs）
 */
const LOG_RENDER_CONFIG = {
  /** YMS标准日志 → 标准渲染，多语种 system='yms' */
  YMS_STANDARD: {renderer: 'standard', dynamicLogSystem: 'yms', useMultilingual: true},
  /** 分销商日志 → 分销商渲染，多语种 system='distributor' */
  DISTRIBUTOR: {renderer: 'distributor', dynamicLogSystem: 'distributor', useMultilingual: true},
  /** YMS查看分销商日志 → 分销商渲染，多语种 system='distributor' */
  YMS_DISTRIBUTOR: {renderer: 'distributor', dynamicLogSystem: 'distributor', useMultilingual: true},
  /** 自营商品日志 → 标准渲染，多语种 system='yms' */
  SELF_OPERATED_PRODUCT: {renderer: 'standard', dynamicLogSystem: 'yms', useMultilingual: true},
  /** 属性池日志 → 自定义结构化渲染，不走 dynamicLog */
  ATTRIBUTE_POOL: {renderer: 'attributePool', dynamicLogSystem: 'yms', useMultilingual: false},
  /** 供应商仓储日志 → 标准渲染，多语种 system='supplier' */
  SUPPLIER_WAREHOUSE: {renderer: 'standard', dynamicLogSystem: 'supplier', useMultilingual: true},
};

export default {
  name: "commonLogModal",
  mixins: [Mixin],
  props: {
    /**
     * 日志模式标识（必填）
     * 决定了请求接口、参数构建、响应处理、内容渲染的整套策略
     * 可选值：YMS_STANDARD | DISTRIBUTOR | YMS_DISTRIBUTOR | SELF_OPERATED_PRODUCT | ATTRIBUTE_POOL | SUPPLIER_WAREHOUSE
     */
    logMode: {
      type: String,
      required: true
    },
    /**
     * 自定义日志请求接口URL
     * 传入后覆盖 logMode 默认的请求URL，用于同一种模式下不同页面需要调用不同接口的场景
     */
    logApiUrl: {
      type: String,
      default: null
    },
    /**
     * 自定义请求参数对象
     * 传入后覆盖 logMode 默认的 buildParams 方法，用于自定义接口需要不同参数格式的场景
     * 组件会根据 showPage 自动追加分页参数（pageNum、pageSize），调用方只需传入业务参数
     * 示例：{ businessId: '123', types: ['SYSTEM_LOG'] }
     * 通常与 logApiUrl 配合使用，当自定义接口的参数格式与当前 logMode 不一致时传入
     */
    customParams: {
      type: Object,
      default: null
    },
    /** 弹窗标题 */
    title: {
      type: String,
      default: aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000288')
    },
    /** 日志类型数组，传给接口的 types 参数 */
    logType: {
      type: Array,
      default: () => []
    },
    /** 额外需要展示的列 key 值（如 ['operationType']） */
    columnsKeyList: {
      type: Array,
      default: () => []
    },
    /** 是否展示分页，默认不展示 */
    showPage: {
      type: Boolean,
      default: false
    },
    /** 弹窗宽度 */
    modalWidth: {
      type: String,
      default: '800'
    },
    /** 是否需要请求接口获取仓库数据，用于展示仓库相关信息 */
    showWarehouse: {
      type: Boolean,
      default: false
    },
    /**
     * 操作类型，用于区分是spu还是sku日志
     * 仅在 SELF_OPERATED_PRODUCT 模式下使用
     * 可选值：spu、sku
     */
    operationType: {
      type: String,
      default: 'spu'
    },

  },
  data() {
    let v = this;
    return {
      pageParamsStatus: false,
      commonLogModal: false,
      /** 需要特殊渲染处理的 contentType 列表 */
      specialLogContentTypeList: [157],
      originalColumns: [
        {
          title: aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000289'),
          key: 'operationType',
          align: 'left',
          minWidth: 110,
          render(h, params) {
            let content = params.row.content;
            let text = '';
            if (content) {
              if (isJSON(content)) {
                let obj = JSON.parse(content);
                text = obj.operateType;
              }
              return h('span', text);
            }
          }
        },
        {
          title: aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000290'),
          key: 'content',
          align: 'left',
          minWidth: v.modalWidth === '800' ? 250 : 400,
          /**
           * 操作内容列的渲染入口
           * 根据 logMode 对应的 LOG_RENDER_CONFIG 分发到不同的渲染方法：
           *   - standard      → renderStandardContent
           *   - distributor   → renderDistributorContent
           *   - attributePool → renderAttributePoolContent
           */
          render(h, params) {
            const renderConfig = LOG_RENDER_CONFIG[v.logMode];
            switch (renderConfig.renderer) {
              case 'attributePool':
                return v.renderAttributePoolContent(h, params);
              case 'distributor':
                return v.renderDistributorContent(h, params);
              default:
                return v.renderStandardContent(h, params);
            }
          }
        },
        {
          title: aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000291'),
          key: 'updatedTime',
          align: 'center',
          minWidth: 180,
          render: (h, params) => {
            return h('div', v.$uDate.dealTime(params.row.updatedTime, 'fulltime'));
          }
        },
        {
          title: aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000292'),
          minWidth: 120,
          key: 'updatedBy',
          align: 'center',
          /**
           * 操作人列渲染逻辑
           * 基于运行环境（v.system）和日志类型（params.row.type）判断展示内容
           * 注意：此逻辑依赖运行环境而非日志模式，因此保持基于 v.system 判断
           */
          render(h, params) {
            let type = params.row.type || '';
            if (v.system === 'yms' && type === 'DISTRIBUTOR_LOG') {
              return h('div', aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000293'));
            } else if (v.system === 'distributor' && type === 'SYSTEM_LOG') {
              return h('div', aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000294'));
            } else if (['yms', 'distributor'].includes(v.system) && type === 'SUPPLIER_LOG') {
              return h('div', aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000295'));
            } else {
              return h('div', getUserNameByUserId(params.row.updatedBy, v.userInfoList, v.$route.path));
            }
          }
        }
      ],
      tableColumns: [],
      tableData: [],
      /** 默认列表展示字段的 key 值 */
      defaultColumnsKeyList: ['content', 'updatedTime', 'updatedBy'],
      pageParams: {
        pageNum: 1,
        pageSize: 15,
        businessId: ''
      },
      total: 0,
      logisticsData: [],
      userInfoList: {},
    };
  },
  methods: {
    /**
     * 初始化日志弹窗数据
     * 流程：
     *   1. 分页模式下重置页码和 businessId
     *   2. 获取用户信息（用于操作人列展示）
     *   3. 如需仓库数据则请求并缓存
     *   4. 处理列配置
     *   5. 请求日志数据
     *   6. 打开弹窗
     */
    initCommonLogData(businessId) {
      let v = this;
      // 分页模式需要重置页码和 businessId
      // 自定义模式时以 showPage 为准，否则以 logMode 配置为准
      const isPaged = v.customParams ? v.showPage : LOG_REQUEST_CONFIG[v.logMode].isPagedResult;
      if (isPaged) {
        v.pageParams.pageNum = 1;
        v.pageParams.pageSize = 15;
      }
      v.pageParams.businessId = businessId || null;
      (async () => {
        await getAllUsersInfo().then((data) => {
          v.userInfoList = data;
        });
        if (v.showWarehouse) {
          await getAllWarehouseData({servicePlatforms: ['core', 'supplier']}).then((warehouseData) => {
            commonLocalStorage.setItem('warehouseCacheData', warehouseData);
          });
        }
        v.handleColumns();
        v.getLogData(businessId);
      })();
      v.commonLogModal = true;
    },

    /**
     * 获取日志数据（统一入口）
     * 根据 logMode 从 LOG_REQUEST_CONFIG 获取请求策略
     * 根据 logMode 从 LOG_RENDER_CONFIG 获取多语种处理参数
     *
     * 流程：
     *   1. 确定请求URL（logApiUrl prop 优先，否则用模式默认URL）
     *   2. 构建请求参数（customParams prop 优先，否则用 logMode 默认 buildParams；showPage 时自动追加分页参数）
     *   3. 发送请求，提取列表数据
     *   4. 多语种处理（统一管线，属性池模式除外）
     *   5. 过滤处理（空内容过滤、contentType=48 过滤）
     *   6. 更新分页数据
     */
    getLogData(businessId) {
      const v = this;
      const requestConfig = LOG_REQUEST_CONFIG[v.logMode];
      const renderConfig = LOG_RENDER_CONFIG[v.logMode];

      v.tableLoading = true;
      v.tableData = [];

      // URL优先级：logApiUrl prop > 模式默认URL
      const url = v.logApiUrl || requestConfig.getDefaultUrl();
      // 参数构建优先级：customParams prop > logMode 默认 buildParams
      // showPage 为 true 时自动追加分页参数，调用方无需关心
      let params;
      if (v.customParams) {
        params = v.showPage
          ? {...v.customParams, pageNum: v.pageParams.pageNum, pageSize: v.pageParams.pageSize}
          : {...v.customParams};
      } else {
        params = requestConfig.buildParams(businessId, v);
      }

      // 自定义模式时以 showPage 为准，否则以 logMode 配置为准
      const isPaged = v.customParams ? v.showPage : requestConfig.isPagedResult;

      v.axios.post(url, params).then(({data}) => {
        if (data.code === 0) {
          const rawData = data.datas || {};
          // 1. 提取列表数据（分页响应取 list，非分页取整个 datas）
          let list = isPaged
            ? (rawData.list || [])
            : requestConfig.extractList(rawData);
          // 2. 多语种处理（统一管线，属性池模式跳过）
          if (renderConfig.useMultilingual) {
            list.forEach((item) => {
              item.content = item.content || item.operateContent;
            })
            list = v.handleMultilingualLogs(list, renderConfig.dynamicLogSystem);
          }
          // 3. 过滤空 logContent（YMS_DISTRIBUTOR 模式需要）
          if (requestConfig.filterEmpty) {
            list = list.filter(item => item.logContent);
          }
          // 4. 分销商系统过滤 contentType=48（调价日志在分销商端不展示）
          if (v.system === 'distributor') {
            list = list.filter(item => item.contentType !== 48);
          }
          v.tableData = list;
          // 5. 分页数据更新
          if (isPaged) {
            v.total = rawData.total || 0;
          }
          v.$forceUpdate();
        }
      }).finally(() => {
        v.tableLoading = false;
      });
    },

    /**
     * 处理列表需要展示的字段
     * 根据 columnsKeyList + defaultColumnsKeyList 过滤 originalColumns
     */
    handleColumns() {
      let v = this;
      let keyList = [...v.columnsKeyList, ...v.defaultColumnsKeyList];
      v.tableColumns = v.originalColumns.filter((item) => {
        return keyList.includes(item.key);
      });
      v.$forceUpdate();
    },

    /**
     * 监听弹窗关闭事件
     * 关闭时清空表格数据，移除仓库缓存
     */
    commonLogChange(value) {
      if (!value) {
        this.tableData = [];
        if (this.showWarehouse) {
          commonLocalStorage.removeItem('warehouseCacheData');
        }
      }
    },
    /**
     * 标准渲染方法
     * 适用模式：YMS_STANDARD、SELF_OPERATED_PRODUCT、SUPPLIER_WAREHOUSE
     *
     * 渲染逻辑：
     *   - content 是 JSON 且有 describe 字段 → 展示 operateContent + describe（格式化换行）
     *   - content 是 JSON 但无 describe → 降级到 logContent 展示
     *   - logContent 存在 → 展示 logContent（数组或字符串）
     *   - 兜底 → content 按 ; 换行展示
     */
    renderStandardContent(h, params) {
      let v = this;
      let content = params.row.content;
      let logContent = params.row.logContent || '';

      if (content) {
        if (isJSON(content)) {
          let obj = JSON.parse(content);
          let operateContent = obj.operateContent;
          let describe = '';
          if (obj.describe) {
            // 有 describe 字段，格式化展示
            let text = obj.describe.replace(/：/g, '：<br/>');
            describe = text.replace(/\n/g, '<br/>');
            return h('div', {class: 'flex flex-direction'}, [
              operateContent ? h('p', {class: 'mt5 mb5'}, operateContent) : '',
              describe ? h('p', {
                class: 'mb5',
                domProps: {innerHTML: describe}
              }) : ''
            ]);
          } else {
            // 无 describe，降级到 logContent
            return v.renderLogContent(h, logContent);
          }
        } else {
          if (logContent) {
            return v.renderLogContent(h, logContent);
          } else {
            // 兜底：用 ; 换行展示
            content = content.replace(/;/g, '<br/>');
            return h('div', {
              domProps: {innerHTML: content}
            });
          }
        }
      }
    },

    /**
     * 分销商渲染方法
     * 适用模式：DISTRIBUTOR、YMS_DISTRIBUTOR
     *
     * 渲染逻辑：
     *   - logContent 是数组：
     *     - contentType === 157 → handlelogContent 金额修改渲染
     *     - contentType === 173 → 每行 + "查看详情"按钮（打开 platformOrderLogDetailsModal）
     *     - 其他 → 每行 <p> 展示
     *   - logContent 非数组 → 单行 <p> 展示
     */
    renderDistributorContent(h, params) {
      let v = this;
      let logContent = params.row.logContent || '';
      let contentType = params.row.contentType;

      let list = [];
      if (Array.isArray(logContent)) {
        // 特殊 contentType 走独立处理
        if (v.specialLogContentTypeList.includes(contentType)) {
          return v.handlelogContent(h, contentType, logContent);
        } else {
          logContent.map((item) => {
            list.push(
              h('p', {class: 'mt3 mb3'}, item),
              // contentType=173 平台订单日志：添加"查看详情"按钮
              contentType === 173 ? h('span', {
                class: 'cursor distributorThemeColor ml8',
                on: {
                  click() {
                    v.$refs['platformOrderLogDetails'].initPlatformOrderLogDetailsData(params.row);
                  }
                }
              }, aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000296')) : ''
            );
          });
          return h('div', {class: 'flex flex-wrap'}, list);
        }
      } else {
        return h('p', {class: 'mt5 mb5'}, logContent);
      }
    },

    /**
     * 属性池渲染方法
     * 适用模式：ATTRIBUTE_POOL
     *
     * 渲染逻辑：
     *   - content 是 JSON → 解析 editAttribute 和 editAttributevalue，结构化对比展示
     *   - content 非 JSON → 展示 logContent 或原始 content
     *
     * 注意：此方法保留原有的完整业务逻辑，不做改动
     */
    renderAttributePoolContent(h, params) {
      let v = this;
      let content = params.row.content;
      let logContent = params.row.logContent || '';

      if (isJSON(content)) {
        return v.handleAttributePoolCustomContent(h, content);
      } else {
        if (logContent) {
          return h('div', logContent);
        } else {
          return h('div', content);
        }
      }
    },

    /**
     * 渲染 logContent 的公共方法
     * 数组展示为多个 <p>，字符串展示为单个 <p>
     */
    renderLogContent(h, logContent) {
      if (Array.isArray(logContent)) {
        let list = [];
        logContent.map((item) => {
          list.push(h('p', {class: 'mt3 mb3'}, item));
        });
        return h('div', {class: 'flex flex-wrap'}, list);
      } else {
        return h('p', {class: 'mt5 mb5'}, logContent);
      }
    },

    /**
     * 处理特殊日志内容（按 contentType 分发）
     * 当前处理：
     *   - contentType=157：货品金额修改（处理费/物流费修改的原价和修改后价格对比）
     */
    handlelogContent(h, contentType, logContent) {
      switch (contentType) {
        case 157:
          let arr = [];
          logContent.map((item) => {
            let name = item.type === 2 ? aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000297') : item.type === 4 ? aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000298') : item.name;
            arr.push(
              h('div', [
                h('p', {class: 'mt2 mb2 font-weight-bold color-333'}, name),
                h('p', {class: 'mt2 mb2 flex align-items-center'}, [
                  h('span', aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000299') + item.originalAmount + ' ' + '' + this.currencyState),
                  h('span', {class: 'ml40'}, aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000300') + item.amount + '' + this.currencyState)
                ]),
              ])
            );
          });
          return h('div', {class: 'flex flex-direction'}, [
            h('p', {class: 'font-weight-bold'}, aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000301')),
            ...arr
          ]);
          break;
      }
    },

    /**
     * 处理属性池操作日志内容
     * 解析 editAttribute（编辑属性）和 editAttributevalue（编辑属性值）
     * 展示原值和修改后的对比
     *
     * 注意：此方法为属性池专用，保留原有完整业务逻辑
     */
    handleAttributePoolCustomContent(h, content) {
      if (content) {
        let attribute = '';
        let attributevalue = '';
        let obj = JSON.parse(content);
        if (obj) {
          // 编辑属性
          let editAttributeList = obj.editAttribute || [];
          if (editAttributeList.length > 0) {
            let keyList = ['cnName', 'deName', 'usName', 'ukName', 'auName', 'euName', 'esName', 'frName', 'itName', 'nlName', 'plName', 'ptName'];
            let beforeItem = {};
            let afterItem = {};
            for (const key of keyList) {
              // 判断每一对属性值是否都为空
              const areBothEmpty = editAttributeList[0][key] === 'null' && editAttributeList[1][key] === 'null';
              // 如果都为空，则过滤掉这个属性
              if (!areBothEmpty) {
                beforeItem[key] = editAttributeList[0][key] !== 'null' ? editAttributeList[0][key] : '';
                afterItem[key] = editAttributeList[1][key] !== 'null' ? editAttributeList[1][key] : '';
              }
            }
            // 编辑前
            let beforeAttributeList = handleAttributeData(beforeItem, editAttributeList[0], aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000302'));
            // 编辑后
            let afterAttributeList = handleAttributeData(afterItem, editAttributeList[1], aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000300'));
            attribute = h('div', [
              h('p', {class: 'mt5 mb5 font-weight-bold'}, aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000303')),
              h('div', {class: 'flex mb10'}, [
                h('div', {class: 'mr50'}, beforeAttributeList),
                h('div', afterAttributeList),
              ])
            ]);

            function handleAttributeData(data, obj, title) {
              let arr = [];
              let list = [];
              for (let key in data) {
                if (keyList.includes(key)) {
                  let name = '';
                  ATTRIBUTEMULTILINGUALNAME.map((ele) => {
                    if (ele.key === key) {
                      name = ele.name;
                      let text = `${aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000286')}${name}：${data[key]}`;
                      list.push(
                        h('p', text)
                      );
                    }
                  });
                }
              }
              let attributeRequireValue = [aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000304'), aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000305'), aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000306')];
              arr.push(
                h('p', title || data.title),
                obj.attributeType !== 'null' ? h('p', obj.attributeType === '1' ? aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000307') : aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000308')) : '',
                obj.chooseType !== 'null' ? h('p', obj.chooseType === '0' ? aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000309') : aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000310')) : '',
                obj.attributeRequire !== 'null' ? h('p', attributeRequireValue[obj.attributeRequire] || '') : '',
                obj.isSizeAttribute !== 'null' ? h('p', obj.isSizeAttribute === '1' ? aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000311') : aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000312')) : '',
                obj.isAllowCustomValue !== 'null' ? h('p', obj.isAllowCustomValue === '1' ? aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000313') : aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000314')) : '',
                ...list
              );
              return arr;
            }
          }
          // 编辑属性值
          let editAttributevalueList = obj.editAttributevalue || [];
          if (editAttributevalueList.length > 0) {
            let keyList = ['cnValue', 'usValue', 'ukValue', 'auValue', 'euValue', 'frValue', 'deValue', 'esValue', 'nlValue', 'plValue', 'ptValue', 'itValue'];
            let beforeAttributevalueItem = {
              title: '',
              valueList: []
            };
            let afterAttributevalueItem = {
              title: '',
              valueList: []
            };
            let valueBeforeData = editAttributevalueList[0].valueList;
            let valueAfterData = editAttributevalueList[1].valueList;
            for (let i = 0; i < valueBeforeData.length; i++) {
              for (let j = 0; j < valueAfterData.length; j++) {
                const valueBefore = valueBeforeData[i];
                const valueAfter = valueAfterData[j];
                for (const key of keyList) {
                  // 判断每一对属性值是否都为空
                  const areBothEmpty = valueBefore[key] === 'null' && valueAfter[key] === 'null';
                  // 如果都为空，则过滤掉这个属性值
                  if (!areBothEmpty) {
                    beforeAttributevalueItem.title = editAttributevalueList[0].title;
                    beforeAttributevalueItem.valueList.push({
                      [key]: valueBefore[key]
                    });
                    afterAttributevalueItem.title = editAttributevalueList[1].title;
                    afterAttributevalueItem.valueList.push({
                      [key]: valueAfter[key]
                    });
                  }
                }
              }
            }

            // 过滤掉重复的属性值
            function deduplicateValueList(valueList) {
              let uniqueValues = new Set();
              return valueList.filter((value) => {
                const key = Object.keys(value)[0];
                if (!uniqueValues.has(value[key])) {
                  uniqueValues.add(value[key]);
                  return true;
                }
                return false;
              });
            }

            beforeAttributevalueItem.valueList = deduplicateValueList(beforeAttributevalueItem.valueList);
            afterAttributevalueItem.valueList = deduplicateValueList(afterAttributevalueItem.valueList);
            // 编辑前
            let beforeAttributevalueList = handleAttributevalueData(beforeAttributevalueItem, aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000302'));
            // 编辑后
            let afterAttributevalueList = handleAttributevalueData(afterAttributevalueItem, aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000300'));
            attributevalue = h('div', [
              h('p', {class: attribute ? ' mt20 mb5 font-weight-bold' : 'mt5 mb5 font-weight-bold'}, aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000315')),
              h('div', {class: 'flex mb10'}, [
                h('div', {class: 'mr50'}, beforeAttributevalueList),
                h('div', afterAttributevalueList),
              ])
            ]);

            function handleAttributevalueData(data, title) {
              let arr = [];
              let list = [];
              let valueList = data.valueList || [];
              if (valueList.length > 0) {
                valueList.map((val) => {
                  for (let key in val) {
                    if (keyList.includes(key)) {
                      let name = '';
                      ATTRIBUTEVALUEDATA.map((ele) => {
                        if (ele.key === key) {
                          name = ele.name;
                          let text = val[key] !== 'null' ? `${aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000287')}${name}：${val[key]}` : `${aliasb03645cf6abd4d56a73b13687765a5d8.t('key1000287')}${name}：`;
                          list.push(
                            h('p', text)
                          );
                        }
                      });
                    }
                  }
                });
              }
              arr.push(
                h('p', title || data.title),
                ...list
              );
              return arr;
            }
          }

          return h('div', [
            attribute,
            attributevalue
          ]);
        }
      }
    },
  },
  watch: {
    pageParamsStatus(value) {
      let v = this;
      if (value) {
        v.getLogData();
        v.pageParamsStatus = false;
      }
    }
  },
  components: {
    platformOrderLogDetailsModal
  }
};
</script>

<style lang="less" scoped>

</style>
